import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X, ArrowUpRight } from "lucide-react";
import { Logo } from "./Logo";
import { scrollToId } from "../lib/nav";

const LINKS = [
  { label: "Services", href: "#services" },
  { label: "Us", href: "#us" },
  { label: "Where", href: "#location" },
  { label: "Reviews", href: "#reviews" },
  { label: "Toolkit", href: "#toolkit" },
];

export { scrollToId };

export const NavHeader = () => {
  const [open, setOpen] = useState(false);

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    scrollToId(href);
  };

  return (
    <header
      data-testid="nav-header"
      className="fixed top-0 inset-x-0 z-50 bg-[#FDFBF7]/80 backdrop-blur-xl border-b border-[#103080]/10"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
        <a
          href="#top"
          data-testid="nav-logo-link"
          onClick={(e) => go(e, "#top")}
          className="shrink-0"
          aria-label="Rosevale Accountants home"
        >
          <Logo />
        </a>

        <nav className="hidden lg:flex items-center gap-6" aria-label="Primary">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              onClick={(e) => go(e, l.href)}
              className="text-sm font-medium text-[#0F172A]/70 hover:text-[#0090D0] transition-colors duration-300 tracking-wide"
            >
              {l.label}
            </a>
          ))}
          <a
            href="https://rosevaleaccountantsltd.portal.engager.app"
            target="_blank"
            rel="noreferrer"
            data-testid="nav-portal-link"
            className="text-sm font-medium text-[#0F172A]/70 hover:text-[#0090D0] transition-colors duration-300 tracking-wide inline-flex items-center gap-1"
          >
            Client Portal <ArrowUpRight size={14} />
          </a>
        </nav>

        <div className="hidden lg:flex items-center gap-5">
          <a
            href="tel:01173210360"
            data-testid="nav-phone-link"
            className="inline-flex items-center gap-2 text-sm font-medium text-[#103080] hover:text-[#0090D0] transition-colors duration-300"
          >
            <Phone size={15} /> 0117 321 0360
          </a>
          <a
            href="https://calendly.com/abbie-browne"
            target="_blank"
            rel="noreferrer"
            data-testid="nav-book-call-button"
            className="group inline-flex items-center gap-2 rounded-full bg-[#103080] text-white text-sm font-medium px-6 py-3 hover:bg-[#0090D0] transition-colors duration-300"
          >
            Book a discovery call
            <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </div>

        <button
          data-testid="nav-mobile-menu-button"
          onClick={() => setOpen(!open)}
          className="lg:hidden p-2 text-[#103080]"
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden bg-[#FDFBF7]/95 backdrop-blur-xl border-t border-[#103080]/10"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  onClick={(e) => go(e, l.href)}
                  className="font-serif text-2xl text-[#103080]"
                >
                  {l.label}
                </a>
              ))}
              <a
                href="https://rosevaleaccountantsltd.portal.engager.app"
                target="_blank"
                rel="noreferrer"
                data-testid="nav-mobile-portal-link"
                className="font-serif text-2xl text-[#103080]"
              >
                Client Portal
              </a>
              <a
                href="https://calendly.com/abbie-browne"
                target="_blank"
                rel="noreferrer"
                data-testid="nav-mobile-book-call-button"
                className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-[#103080] text-white text-sm font-medium px-6 py-4"
              >
                Book a discovery call <ArrowUpRight size={16} />
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
